import React, { useState, useEffect, useCallback, useRef } from 'react';
import useEscapeKey from '../utils/useEscapeKey';
import Footer from '../components/Footer';
import Select from '../components/Select';
import { useInvoice } from '../context/InvoiceContext';
import { useConfirm } from '../context/ConfirmContext';
import { todoService } from '../services/api';

const FILES_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

const PERF_META = {
  exceeds_expectations: { label: 'Good Job',           color: '#166534', bg: '#DCFCE7', border: '#BBF7D0' },
  needs_improvement:    { label: 'Can Do Better',      color: '#9A3412', bg: '#FFEDD5', border: '#FDBA74' },
  below_expectations:   { label: 'Below Expectations', color: '#991B1B', bg: '#FEE2E2', border: '#FECACA' },
};

const STATUS_META = {
  todo:        { label: 'TODO',        color: '#475569', bg: '#E2E8F0' },
  in_progress: { label: 'IN PROGRESS', color: '#0369A1', bg: '#BAE6FD' },
  blocked:     { label: 'BLOCKED',     color: '#B91C1C', bg: '#FECACA' },
  in_review:   { label: 'IN REVIEW',   color: '#B45309', bg: '#FDE68A' },
  follow_up:   { label: 'FOLLOW UP',   color: '#C2410C', bg: '#FFEDD5' },
  completed:   { label: 'DONE',        color: '#047857', bg: '#A7F3D0' },
};

const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const userName = (u) => u ? [u.firstName, u.lastName].filter(Boolean).join(' ') : '—';

const StatusBadge = ({ status }) => {
  const sm    = STATUS_META[status] || STATUS_META.todo;
  const color = sm.color;
  const bg    = sm.bg;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '3px 7px', borderRadius: '2px', fontSize: '11px',
      fontWeight: '600', letterSpacing: '0.04em', lineHeight: 1.4,
      color, background: bg, border: `1px solid ${color}44`,
      whiteSpace: 'nowrap',
    }}>
      {sm.label}
    </span>
  );
};

const StatusDropdown = ({ status, onChange }) => {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const ref = useRef(null);
  const btnRef = useRef(null);
  const sm    = STATUS_META[status] || STATUS_META.todo;
  const color = sm.color;
  const bg    = sm.bg;

  useEffect(() => {
    if (!open) return;
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < 180;
      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        minWidth: '160px',
        zIndex: 9999,
        ...(openUpward
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    }
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'block', width: '100%' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '3px 7px', borderRadius: '2px',
          border: `1px solid ${color}44`, background: bg,
          color, fontWeight: '600',
          fontSize: '11px', letterSpacing: '0.04em',
          cursor: 'pointer', whiteSpace: 'nowrap', outline: 'none', lineHeight: 1.4
        }}
      >
        {sm.label}
        <svg width="7" height="4" viewBox="0 0 7 4" style={{ opacity: 0.55, flexShrink: 0 }}>
          <path d="M0 0l3.5 4L7 0z" fill="currentColor" />
        </svg>
      </button>

      {open && (
        <div className="custom-select-list" style={dropdownStyle}>
          {Object.entries(STATUS_META).map(([key, meta]) => {
            const isCurrent = status === key;
            const itemColor = meta.color;
            return (
              <button
                key={key}
                type="button"
                className={`custom-select-item${isCurrent ? ' current' : ''}`}
                onClick={() => { setOpen(false); if (!isCurrent) onChange(key); }}
              >
                <span style={{ color: itemColor, fontWeight: '600' }}>{meta.label}</span>
                {isCurrent && (
                  <svg width="11" height="11" viewBox="0 0 12 12" style={{ flexShrink: 0, color: 'var(--color-primary)' }}>
                    <polyline points="1,6 4.5,9.5 11,2.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Performance Dropdown ───────────────────────────────────────────────────────
const PerfDropdown = ({ rating, onChange }) => {
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const ref = useRef(null);
  const btnRef = useRef(null);
  const pm = PERF_META[rating];

  useEffect(() => {
    if (!open) return;
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < 160;
      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        minWidth: '160px',
        zIndex: 9999,
        ...(openUpward
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    }
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'block', width: '100%' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '2px 7px', borderRadius: '2px',
          border: pm ? `1px solid ${pm.border}` : '1px solid var(--color-gray-300)',
          background: pm ? pm.bg : 'transparent',
          color: pm ? pm.color : 'var(--color-text-secondary)',
          fontWeight: pm ? '600' : '400',
          fontSize: '11px', letterSpacing: '0.04em',
          cursor: 'pointer', whiteSpace: 'nowrap', outline: 'none', lineHeight: 1.6
        }}
      >
        {pm ? pm.label : '—'}
        <svg width="7" height="4" viewBox="0 0 7 4" style={{ opacity: 0.55, flexShrink: 0 }}>
          <path d="M0 0l3.5 4L7 0z" fill="currentColor" />
        </svg>
      </button>

      {open && (
        <div className="custom-select-list" style={dropdownStyle}>
          {rating && (
            <button
              type="button"
              className="custom-select-item"
              onClick={() => { setOpen(false); onChange(''); }}
            >
              <span style={{ color: 'var(--color-text-secondary)' }}>— Clear</span>
            </button>
          )}
          {Object.entries(PERF_META).map(([key, meta]) => {
            const isCurrent = rating === key;
            return (
              <button
                key={key}
                type="button"
                className={`custom-select-item${isCurrent ? ' current' : ''}`}
                onClick={() => { setOpen(false); if (!isCurrent) onChange(key); }}
              >
                <span style={{ color: meta.color, fontWeight: '600' }}>{meta.label}</span>
                {isCurrent && (
                  <svg width="11" height="11" viewBox="0 0 12 12" style={{ flexShrink: 0, color: 'var(--color-primary)' }}>
                    <polyline points="1,6 4.5,9.5 11,2.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Create / Edit Modal ────────────────────────────────────────────────────────
const TodoFormModal = ({ onClose, onSave, assignableUsers, editTodo, isManager, currentUser }) => {
  const isEdit = !!editTodo;
  useEscapeKey(onClose);
  const [form, setForm] = useState({
    title: editTodo?.title || '',
    description: editTodo?.description || '',
    assignedToId: editTodo?.assignedToId || '',
    status: editTodo?.status || 'todo',
  });
  const [addReminder, setAddReminder] = useState(false);
  const [reminderDate, setReminderDate] = useState('');
  const [reminderTime, setReminderTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [removeExistingScreenshot, setRemoveExistingScreenshot] = useState(false);

  const handlePaste = (e) => {
    const item = Array.from(e.clipboardData?.items || []).find(i => i.type.startsWith('image/'));
    if (!item) return;
    e.preventDefault();
    const file = item.getAsFile();
    if (!file) return;
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
    setRemoveExistingScreenshot(false);
  };

  useEffect(() => {
    return () => { if (screenshotPreview) URL.revokeObjectURL(screenshotPreview); };
  }, [screenshotPreview]);

  const clearScreenshot = () => {
    setScreenshot(null);
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshotPreview(null);
    if (isEdit && editTodo?.screenshotFilename) setRemoveExistingScreenshot(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    if (isManager && !form.assignedToId) return;
    if (addReminder && !reminderDate) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (!isEdit && addReminder && reminderDate) {
        payload.reminder = {
          dueDate: reminderDate,
          dueTime: reminderTime || null,
        };
      }
      if (screenshot) payload.screenshot = screenshot;
      else if (removeExistingScreenshot) payload.removeScreenshot = true;
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px', overflow: 'visible' }}>
        <div className="flex-between mb-lg">
          <h3>{isEdit ? 'Edit Todo' : 'New Todo'}</h3>
          <button onClick={onClose} className="btn btn-secondary btn-sm">✕</button>
        </div>
        <form onSubmit={handleSubmit} onPaste={handlePaste}>
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input
              className="form-input" autoFocus required
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="What needs to be done?"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-input" rows={3} style={{ resize: 'vertical' }}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Additional details or context..."
            />
          </div>
          <div className="form-group">
            <label className="form-label">Screenshot <span style={{ color: 'var(--color-text-secondary)', fontWeight: 'normal' }}>(optional)</span></label>
            {screenshotPreview ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img src={screenshotPreview} alt="Pasted screenshot" style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-gray-200)', display: 'block' }} />
                <button type="button" onClick={clearScreenshot} className="btn btn-secondary btn-sm" style={{ position: 'absolute', top: '4px', right: '4px', padding: '2px 8px' }}>Remove</button>
              </div>
            ) : (isEdit && editTodo?.screenshotFilename && !removeExistingScreenshot) ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img src={`${FILES_URL}/uploads/${editTodo.screenshotFilename}`} alt="Attached screenshot" style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-gray-200)', display: 'block' }} />
                <button type="button" onClick={clearScreenshot} className="btn btn-secondary btn-sm" style={{ position: 'absolute', top: '4px', right: '4px', padding: '2px 8px' }}>Remove</button>
              </div>
            ) : (
              <div
                tabIndex={0}
                onClick={() => document.getElementById('todo-screenshot-input')?.click()}
                style={{
                  border: '1px dashed var(--color-gray-300)', borderRadius: 'var(--radius-sm)',
                  padding: '12px', textAlign: 'center', fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-secondary)', cursor: 'pointer',
                }}
              >
                Click here and press Ctrl/Cmd+V to paste a screenshot, or
                <input
                  id="todo-screenshot-input"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setScreenshot(file);
                    setScreenshotPreview(URL.createObjectURL(file));
                    setRemoveExistingScreenshot(false);
                  }}
                />
                {' '}<span style={{ color: 'var(--color-primary)', textDecoration: 'underline' }}>choose a file</span>
              </div>
            )}
          </div>
          <div className={isManager ? 'grid grid-cols-2 gap-md' : ''}>
            {isManager && (
              <div className="form-group">
                <label className="form-label">Assign To *</label>
                <Select
                  value={form.assignedToId}
                  onChange={v => setForm(f => ({ ...f, assignedToId: v }))}
                  options={[
                    { value: '', label: '— Select user —' },
                    { value: currentUser?.id, label: `Myself (${userName(currentUser)})` },
                    ...assignableUsers.filter(u => u.id !== currentUser?.id).map(u => ({ value: u.id, label: `${userName(u)}${u.employeeNo ? ` (#${u.employeeNo})` : ''}` })),
                  ]}
                />
              </div>
            )}
            {isEdit && (
              <div className="form-group">
                <label className="form-label">Status</label>
                <StatusDropdown
                  status={form.status}
                  onChange={v => setForm(f => ({ ...f, status: v }))}
                />
              </div>
            )}
          </div>
          {!isEdit && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={addReminder}
                  onChange={e => setAddReminder(e.target.checked)}
                  style={{ width: '15px', height: '15px', cursor: 'pointer' }}
                />
                <span className="form-label" style={{ margin: 0 }}>Add Reminder</span>
              </label>
              {addReminder && (
                <div className="grid grid-cols-2 gap-md" style={{ marginTop: 'var(--spacing-sm)' }}>
                  <div>
                    <label className="form-label">Date *</label>
                    <input
                      type="date"
                      className="form-input"
                      value={reminderDate}
                      onChange={e => setReminderDate(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label">Time <span style={{ color: 'var(--color-text-secondary)', fontWeight: 'normal' }}>(optional)</span></label>
                    <input
                      type="time"
                      className="form-input"
                      value={reminderTime}
                      onChange={e => setReminderTime(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex gap-md">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="flex-center gap-sm"><div className="spinner spinner-sm" />Saving…</span> : (isEdit ? 'Save Changes' : 'Create Todo')}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Complete Modal ─────────────────────────────────────────────────────────────
const CompleteModal = ({ todo, onClose, onConfirm }) => {
  useEscapeKey(onClose);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await onConfirm(comment); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
        <div className="flex-between mb-lg">
          <h3>Mark as Complete</h3>
          <button onClick={onClose} className="btn btn-secondary btn-sm">✕</button>
        </div>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-md)' }}>
          <strong style={{ color: 'var(--color-text-primary)' }}>{todo.title}</strong>
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Completion Comment <span style={{ color: 'var(--color-text-secondary)', fontWeight: 'normal' }}>(optional)</span></label>
            <textarea
              className="form-input" rows={4} autoFocus style={{ resize: 'vertical' }}
              placeholder="Add any notes, outcome, or follow-up info..."
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </div>
          <div className="flex gap-md">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? <span className="flex-center gap-sm"><div className="spinner spinner-sm" />Saving…</span> : 'Mark Complete'}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Performance Badge ──────────────────────────────────────────────────────────
const PerfBadge = ({ rating }) => {
  const pm = PERF_META[rating];
  if (!pm) return null;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '2px',
      fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em',
      color: pm.color, background: pm.bg, border: `1px solid ${pm.border}`
    }}>
      {pm.label}
    </span>
  );
};

// ── Detail Modal ───────────────────────────────────────────────────────────────
const DetailModal = ({ todo, onClose, onEdit, canReview, onReview }) => {
  useEscapeKey(onClose);
  const [reviewForm, setReviewForm] = useState({
    performanceRating: todo.performanceRating || '',
    performanceComment: todo.performanceComment || '',
  });
  const [reviewSaving, setReviewSaving] = useState(false);

  const handleReviewSave = async () => {
    setReviewSaving(true);
    try { await onReview(todo.id, reviewForm); }
    finally { setReviewSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px', maxHeight: 'none', overflowY: 'visible' }}>
        <div className="flex-between mb-lg">
          <h3>Todo Details</h3>
          <div className="flex gap-md">
            <button onClick={onEdit} className="btn btn-outline btn-sm">Edit</button>
            <button onClick={onClose} className="btn btn-secondary btn-sm">✕</button>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
          <div>
            <div className="form-label">Title</div>
            <div style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-lg)' }}>{todo.title}</div>
            {todo.description && (
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: '6px', opacity: 0.7 }}>{todo.description}</div>
            )}
            {todo.screenshotFilename && (
              <a href={`${FILES_URL}/uploads/${todo.screenshotFilename}`} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: '8px' }}>
                <img
                  src={`${FILES_URL}/uploads/${todo.screenshotFilename}`}
                  alt="Attached screenshot"
                  style={{ maxWidth: '100%', maxHeight: '220px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-gray-200)' }}
                />
              </a>
            )}
          </div>
          <div className="grid grid-cols-2 gap-md">
            <div>
              <div className="form-label">Assigned To</div>
              <div>{userName(todo.assignedTo)}</div>
            </div>
            <div>
              <div className="form-label">Assigned By</div>
              <div>{userName(todo.assignedBy)}</div>
            </div>
            <div>
              <div className="form-label">Status</div>
              <StatusBadge status={todo.status} />
            </div>
            <div>
              <div className="form-label">Created</div>
              <div>{fmtDateTime(todo.createdAt)}</div>
            </div>
          </div>
          {todo.status === 'completed' && (
            <>
              <div>
                <div className="form-label">Completed At</div>
                <div>{fmtDateTime(todo.completedAt)}</div>
              </div>
              {todo.completionComment && (
                <div style={{
                  background: 'rgba(22,163,74,0.06)', border: '1px solid rgba(22,163,74,0.2)',
                  borderRadius: '2px', padding: '12px'
                }}>
                  <div className="form-label" style={{ color: '#16a34a', marginBottom: '4px' }}>Completion Comment</div>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--font-size-sm)' }}>{todo.completionComment}</div>
                </div>
              )}
            </>
          )}

          {/* ── Performance Review ── */}
          <div style={{ borderTop: '1px solid var(--color-gray-100)', paddingTop: 'var(--spacing-md)' }}>
            <div style={{
              fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.06em', color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-sm)'
            }}>
              Performance Review
            </div>

            {canReview ? (
              <form onSubmit={e => { e.preventDefault(); handleReviewSave(); }} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                  {[
                    { value: 'exceeds_expectations', label: 'Good Job' },
                    { value: 'needs_improvement',    label: 'Can Do Better' },
                    { value: 'below_expectations',   label: 'Below Expectations' },
                  ].map(opt => {
                    const pm = PERF_META[opt.value];
                    const active = reviewForm.performanceRating === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setReviewForm(f => ({ ...f, performanceRating: active ? '' : opt.value }))}
                        style={{
                          padding: '4px 10px', borderRadius: '2px', fontSize: '11px',
                          fontWeight: 600, letterSpacing: '0.04em', cursor: 'pointer',
                          border: `1px solid ${active ? pm.border : 'var(--color-gray-200)'}`,
                          background: active ? pm.bg : 'transparent',
                          color: active ? pm.color : 'var(--color-text-secondary)',
                          transition: 'all 0.15s',
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <textarea
                  className="form-input" rows={2} style={{ resize: 'vertical' }}
                  placeholder="Add a short comment…"
                  value={reviewForm.performanceComment}
                  onChange={e => setReviewForm(f => ({ ...f, performanceComment: e.target.value }))}
                />
                <button
                  type="submit"
                  className="btn btn-primary btn-sm"
                  style={{ alignSelf: 'flex-end' }}
                  disabled={reviewSaving}
                >
                  {reviewSaving ? 'Saving…' : 'Save Review'}
                </button>
              </form>
            ) : todo.performanceRating ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
                <PerfBadge rating={todo.performanceRating} />
                {todo.performanceComment && (
                  <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: '4px' }}>
                    {todo.performanceComment}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>Not reviewed yet</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────────
const Todos = () => {
  const { currentUser, showNotification, isSuperAdmin } = useInvoice();
  const confirm = useConfirm();
  const isManager = ['admin', 'super_admin'].includes(currentUser?.role);

  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignableUsers, setAssignableUsers] = useState([]);

  // Filters — persisted across page refreshes
  const [filterStatus, setFilterStatus] = useState(() => localStorage.getItem('todoFilterStatus') || '');
  const [filterUserId, setFilterUserId] = useState(() => {
    const saved = localStorage.getItem('todoFilterUserId');
    if (saved !== null) return saved;
    return currentUser?.role === 'super_admin' ? String(currentUser?.id) : '';
  });

  // Drag-and-drop state (manager only)
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editTodo, setEditTodo] = useState(null);
  const [completeTodo, setCompleteTodo] = useState(null);
  const [detailTodo, setDetailTodo] = useState(null);

  const loadTodos = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterUserId === 'exclude_me' && isManager) params.excludeUserId = currentUser?.id;
      else if (filterUserId && isManager) params.assignedToId = filterUserId;
      const data = await todoService.getAll(params);
      setTodos(data);
    } catch (err) {
      showNotification(err.message || 'Failed to load todos', 'error');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterUserId, isManager, currentUser?.id, showNotification]);

  useEffect(() => { loadTodos(); }, [loadTodos]);

  useEffect(() => {
    if (isManager) {
      todoService.getUsers().then(setAssignableUsers).catch(() => {});
    }
  }, [isManager]);

  const handleCreate = async (form) => {
    try {
      const todo = await todoService.create(form);
      setTodos(prev => [todo, ...prev]);
      setShowCreateModal(false);
      showNotification('Todo created', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to create todo', 'error');
    }
  };

  const handleEdit = async (form) => {
    try {
      const updated = await todoService.update(editTodo.id, form);
      setTodos(prev => prev.map(t => t.id === updated.id ? updated : t));
      setEditTodo(null);
      showNotification('Todo updated', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to update todo', 'error');
    }
  };

  const handleComplete = async (comment) => {
    try {
      const updated = await todoService.update(completeTodo.id, { status: 'completed', completionComment: comment });
      setTodos(prev => prev.map(t => t.id === updated.id ? updated : t));
      setCompleteTodo(null);
      showNotification('Marked as complete', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to update todo', 'error');
    }
  };

  const handleReopen = async (todo) => {
    try {
      const updated = await todoService.update(todo.id, { status: 'todo' });
      setTodos(prev => prev.map(t => t.id === updated.id ? updated : t));
      showNotification('Todo reopened', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to reopen todo', 'error');
    }
  };

  const handleStatusChange = async (todo, newStatus) => {
    try {
      const updated = await todoService.update(todo.id, { status: newStatus });
      setTodos(prev => prev.map(t => t.id === updated.id ? updated : t));
      showNotification(`Status updated to ${STATUS_META[newStatus]?.label || newStatus}`, 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to update status', 'error');
    }
  };

  const handleDragStart = (e, todoId) => {
    setDraggedId(todoId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, todoId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (todoId !== draggedId) setDragOverId(todoId);
  };

  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    const newTodos = [...todos];
    const fromIndex = newTodos.findIndex(t => t.id === draggedId);
    const toIndex = newTodos.findIndex(t => t.id === targetId);
    const [moved] = newTodos.splice(fromIndex, 1);
    newTodos.splice(toIndex, 0, moved);
    setTodos(newTodos);
    setDraggedId(null);
    setDragOverId(null);
    try {
      await todoService.reorder(newTodos.map(t => t.id));
    } catch (err) {
      showNotification('Failed to save order', 'error');
      loadTodos();
    }
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleDelete = async (todo) => {
    const ok = await confirm(`Delete "${todo.title}"? This cannot be undone.`, {
      title: 'Delete Todo', confirmLabel: 'Delete', danger: true
    });
    if (!ok) return;
    try {
      await todoService.delete(todo.id);
      setTodos(prev => prev.filter(t => t.id !== todo.id));
      showNotification('Todo deleted', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to delete todo', 'error');
    }
  };

  const handleReview = async (id, { performanceRating, performanceComment }) => {
    try {
      const updated = await todoService.review(id, { performanceRating, performanceComment });
      setTodos(prev => prev.map(t => t.id === updated.id ? updated : t));
      if (detailTodo?.id === id) setDetailTodo(updated);
      showNotification('Performance review saved', 'success');
    } catch (err) {
      showNotification(err.message || 'Failed to save review', 'error');
    }
  };

  const todo = todos.filter(t => t.status === 'todo');
  const inProgress = todos.filter(t => t.status === 'in_progress');
  const blocked = todos.filter(t => t.status === 'blocked');
  const inReview = todos.filter(t => t.status === 'in_review');

  const tdStyle = { padding: 'var(--spacing-sm) var(--spacing-md)', verticalAlign: 'middle', fontSize: 'var(--font-size-sm)' };

  return (
    <>
      <div className="main-content">
        <div className="container">



          {/* Header row */}
          <div className="flex-between mb-lg" style={{ flexWrap: 'wrap', gap: 'var(--spacing-md)' }}>
            <h1>Todos</h1>
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              + New Todo
            </button>
          </div>

          {/* Stats */}
          <div style={{ display: 'flex', gap: 'var(--spacing-md)', marginBottom: 'var(--spacing-lg)', flexWrap: 'wrap' }}>
            {[
              { label: 'TODO',        value: todo.length,       color: STATUS_META.todo.color        },
              { label: 'IN PROGRESS', value: inProgress.length, color: STATUS_META.in_progress.color },
              { label: 'BLOCKED',     value: blocked.length,    color: STATUS_META.blocked.color     },
              { label: 'IN REVIEW',   value: inReview.length,   color: STATUS_META.in_review.color   },
            ].map(s => (
              <div key={s.label} className="card" style={{ padding: '12px 16px', textAlign: 'center', flex: '1 1 80px' }}>
                <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: '700', color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: '2px' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="card mb-lg">
            <div style={{ display: 'flex', gap: 'var(--spacing-md)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 140px' }}>
                <label className="form-label">Status</label>
                <Select
                  value={filterStatus}
                  onChange={v => { setFilterStatus(v); localStorage.setItem('todoFilterStatus', v); }}
                  options={[
                    { value: '', label: 'All' },
                    { value: 'todo', label: 'TODO' },
                    { value: 'in_progress', label: 'IN PROGRESS' },
                    { value: 'blocked', label: 'BLOCKED' },
                    { value: 'in_review', label: 'IN REVIEW' },
                    { value: 'completed', label: 'DONE' },
                  ]}
                />
              </div>
              {isManager && assignableUsers.length > 0 && (
                <div style={{ flex: '1 1 180px' }}>
                  <label className="form-label">Assigned To</label>
                  <Select
                    value={filterUserId}
                    onChange={v => { setFilterUserId(v); localStorage.setItem('todoFilterUserId', v); }}
                    options={[
                      { value: '', label: 'All users' },
                      ...(isSuperAdmin() ? [{ value: 'exclude_me', label: 'All except me' }] : []),
                      ...assignableUsers.map(u => ({ value: u.id, label: userName(u) })),
                    ]}
                  />
                </div>
              )}
              {(filterStatus || filterUserId) && (
                <div>
                  <label className="form-label" style={{ visibility: 'hidden' }}>Clear</label>
                  <button
                    className="btn btn-secondary"
                    onClick={() => { setFilterStatus(''); setFilterUserId(''); localStorage.removeItem('todoFilterStatus'); localStorage.removeItem('todoFilterUserId'); }}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Manager table view */}
          {isManager ? (
            <div className="card" style={{ padding: 0 }}>
              {loading ? (
                <div className="flex-center" style={{ padding: 'var(--spacing-xl)' }}>
                  <div className="spinner" />
                </div>
              ) : todos.length === 0 ? (
                <div className="flex-center" style={{ padding: 'var(--spacing-xl)', color: 'var(--color-text-secondary)' }}>
                  No todos found.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                        <th style={{ ...tdStyle, width: '28px', color: 'var(--color-text-secondary)' }} title="Drag to reorder">⠿</th>
                        {['Title', 'Assigned To', 'Status', 'Performance', 'Actions'].map(h => (
                          <th key={h} style={{ ...tdStyle, fontWeight: '600', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', textAlign: h === 'Actions' ? 'right' : 'left' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {todos.map(todo => (
                        <tr
                          key={todo.id}
                          draggable
                          className="todo-row"
                          onDragStart={e => handleDragStart(e, todo.id)}
                          onDragOver={e => handleDragOver(e, todo.id)}
                          onDrop={e => handleDrop(e, todo.id)}
                          onDragEnd={handleDragEnd}
                          onClick={() => setDetailTodo(todo)}
                          style={{
                            borderBottom: dragOverId === todo.id
                              ? '2px solid #2563EB'
                              : '1px solid var(--color-gray-200)',
                            ...(dragOverId === todo.id && { backgroundColor: 'rgba(37,99,235,0.12)' }),
                            opacity: draggedId === todo.id ? 0.15 : (todo.status === 'completed' ? 0.7 : 1),
                            cursor: draggedId ? 'grabbing' : 'pointer',
                            transition: 'border-color 0.1s'
                          }}
                        >
                          <td style={{ ...tdStyle, color: 'var(--color-gray-400)', fontSize: '16px', userSelect: 'none' }}>⠿</td>
                          <td style={{ ...tdStyle, maxWidth: '260px' }}>
                            <div style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--color-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {todo.title}
                            </div>
                          </td>
                          <td style={{ ...tdStyle, maxWidth: '160px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{userName(todo.assignedTo)}</td>
                          <td style={{ ...tdStyle, width: '140px' }} onClick={e => e.stopPropagation()}>
                            {todo.status === 'completed' ? (
                              <StatusBadge status={todo.status} />
                            ) : (
                              <StatusDropdown
                                status={todo.status}
                                onChange={v => {
                                  if (v === 'completed') {
                                    if (isSuperAdmin()) handleStatusChange(todo, 'completed');
                                    else setCompleteTodo(todo);
                                  } else handleStatusChange(todo, v);
                                }}
                              />
                            )}
                          </td>
                          <td style={{ ...tdStyle, width: '130px' }} onClick={e => e.stopPropagation()}>
                            {isSuperAdmin() ? (
                              <PerfDropdown
                                rating={todo.performanceRating || ''}
                                onChange={v => handleReview(todo.id, { performanceRating: v, performanceComment: todo.performanceComment || '' })}
                              />
                            ) : (
                              todo.performanceRating ? <PerfBadge rating={todo.performanceRating} /> : <span style={{ color: 'var(--color-text-secondary)', fontSize: '12px' }}>—</span>
                            )}
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <div className="flex gap-md" style={{ justifyContent: 'flex-end' }}>
                              {todo.status === 'completed' && (
                                <button className="btn btn-secondary btn-sm" onClick={e => { e.stopPropagation(); handleReopen(todo); }}>Reopen</button>
                              )}
                              <button
                                className="btn btn-sm"
                                style={{ background: 'rgba(220,38,38,0.1)', color: '#DC2626', border: '1px solid rgba(220,38,38,0.2)' }}
                                onClick={e => { e.stopPropagation(); handleDelete(todo); }}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            /* Employee card view */
            loading ? (
              <div className="flex-center" style={{ padding: 'var(--spacing-xl)' }}>
                <div className="spinner" />
              </div>
            ) : todos.length === 0 ? (
              <div className="card flex-center" style={{ padding: 'var(--spacing-xl)', color: 'var(--color-text-secondary)' }}>
                You have no todos assigned.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {todos.map(todo => {
                  const sm = STATUS_META[todo.status] || STATUS_META.todo;
                  const done = todo.status === 'completed';
                  return (
                    <div
                      key={todo.id}
                      className="card"
                      onClick={() => setDetailTodo(todo)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '12px',
                        flexWrap: 'nowrap',
                        padding: '10px 14px',
                        borderLeft: `3px solid ${sm.color}`,
                        background: done ? 'rgba(22,163,74,0.06)' : undefined,
                        cursor: 'pointer',
                      }}
                    >
                      {/* Title */}
                      <div style={{
                        flex: 1, fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        textDecoration: done ? 'line-through' : 'none', minWidth: 0,
                      }}>
                        {todo.title}
                      </div>

                      {/* Status column */}
                      <div style={{ flexShrink: 0, width: '130px', display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                        {done ? (
                          <StatusBadge status={todo.status} />
                        ) : (
                          <StatusDropdown
                            status={todo.status}
                            onChange={v => {
                              if (v === 'completed') {
                                if (isSuperAdmin()) handleStatusChange(todo, 'completed');
                                else setCompleteTodo(todo);
                              } else handleStatusChange(todo, v);
                            }}
                          />
                        )}
                      </div>

                      {/* Performance review */}
                      <div style={{ flexShrink: 0, width: '76px' }}>
                        {todo.performanceRating && <PerfBadge rating={todo.performanceRating} />}
                      </div>

                      {/* Assigned by */}
                      <div style={{ flexShrink: 0, width: '100px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {userName(todo.assignedBy)}
                      </div>

                      {/* Actions */}
                      <div style={{ flexShrink: 0, width: '210px', display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'flex-end' }} onClick={e => e.stopPropagation()}>
                        {done && (
                          <button className="btn btn-secondary btn-sm" onClick={() => handleReopen(todo)}>Reopen</button>
                        )}
                        {(todo.assignedById === currentUser?.id || todo.assignedTo?.id === currentUser?.id) && (
                          <>
                            <button className="btn btn-outline btn-sm" onClick={() => setEditTodo(todo)}>Edit</button>
                            {todo.assignedById === currentUser?.id && (
                              <button
                                className="btn btn-sm"
                                style={{ background: 'rgba(220,38,38,0.1)', color: '#DC2626', border: '1px solid rgba(220,38,38,0.2)' }}
                                onClick={() => handleDelete(todo)}
                              >
                                Delete
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>
      <Footer />

      {showCreateModal && (
        <TodoFormModal
          assignableUsers={assignableUsers}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreate}
          isManager={isManager}
          currentUser={currentUser}
        />
      )}
      {editTodo && (
        <TodoFormModal
          editTodo={editTodo}
          assignableUsers={assignableUsers}
          onClose={() => setEditTodo(null)}
          onSave={handleEdit}
          isManager={isManager}
          currentUser={currentUser}
        />
      )}
      {completeTodo && (
        <CompleteModal
          todo={completeTodo}
          onClose={() => setCompleteTodo(null)}
          onConfirm={handleComplete}
        />
      )}
      {detailTodo && (
        <DetailModal
          todo={detailTodo}
          onClose={() => setDetailTodo(null)}
          onEdit={() => { setEditTodo(detailTodo); setDetailTodo(null); }}
          canReview={isSuperAdmin()}
          onReview={handleReview}
        />
      )}
    </>
  );
};

export default Todos;
